<script setup>
// InteliChat #41 - a single HUD row. Leads with the decision fact
// (presence + wait), then the name. A live wait timer whose colour and
// weight climb with the wait (calm -> warm -> hot -> about-to-slip) is
// the honest urgency signal - it comes from the fact, not decoration.
// MISSED rows stay calm (never the hot accent). Click opens the chat.
import { computed } from 'vue';
import { waitSeconds, stageFor, humanWait, rowMicrocopy } from './urgency';

const props = defineProps({
  row: { type: Object, required: true },
  now: { type: Number, default: () => Math.floor(Date.now() / 1000) },
  emphasis: { type: Boolean, default: false },
  // Build #47 Phase 2: MISSED rows expose a jump to the durable ticket the
  // missed chat became (matched by conversation_id in the Tickets workspace).
  linkTicket: { type: Boolean, default: false },
});
const emit = defineEmits(['open', 'ticket']);

const initial = computed(() =>
  (props.row.name || '?').trim().charAt(0).toUpperCase()
);

const secs = computed(() => waitSeconds(props.row.waitingSince, props.now));
const isMissed = computed(() => props.row.bucket === 'missed');

// the escalation ramp stage drives the accent - only the live act-now
// buckets escalate; MISSED is always calm.
const stage = computed(() => (isMissed.value ? 'calm' : stageFor(secs.value)));

// compact live timer for the badge ("6 min", "45 sec", "2 hr")
const waitLabel = computed(() =>
  props.row.waitingSince ? humanWait(secs.value) : ''
);

// the decision line (presence + wait lead), single-sourced phrasing
const line = computed(() =>
  rowMicrocopy(props.row.bucket, props.row.online, props.row.waitingSince, props.now)
);
</script>

<template>
  <button
    class="ic41-row"
    :class="[`ic41-row--${stage}`, { 'ic41-row--emph': emphasis, 'ic41-row--missed': isMissed }]"
    type="button"
    :title="`Open chat with ${row.name}`"
    @click="emit('open', row.id)"
  >
    <span class="ic41-row__av">
      {{ initial }}
      <span v-if="row.online" class="ic41-row__dot" title="Online now" />
    </span>
    <span class="ic41-row__body">
      <span class="ic41-row__top">
        <span class="ic41-row__name">{{ row.name }}</span>
        <span v-if="row.online && !isMissed" class="ic41-row__pres">Online now</span>
      </span>
      <span class="ic41-row__line">{{ line }}</span>
    </span>
    <span
      v-if="linkTicket"
      class="ic41-row__ticket"
      role="button"
      tabindex="0"
      title="Open the ticket for this missed chat"
      @click.stop="emit('ticket', row.id)"
      @keydown.enter.stop="emit('ticket', row.id)"
    >Ticket</span>
    <span
      v-if="waitLabel"
      class="ic41-row__wait"
      :class="`ic41-row__wait--${stage}`"
    >{{ waitLabel }}</span>
  </button>
</template>

<style scoped lang="scss">
.ic41-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-left: 3px solid transparent;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.14s ease, border-color 0.14s ease;

  &:hover {
    background: var(--il-silver-soft, #edeff3);
  }

  &--emph {
    background: var(--il-accent-soft, #f2eafe);
    &:hover {
      background: color-mix(in srgb, var(--il-accent-soft, #f2eafe) 82%, #000 6%);
    }
  }

  // escalation ramp: the left accent bar warms with the wait. Reserved for
  // the live act-now buckets; MISSED never lights the hot accent.
  &--warm {
    border-left-color: var(--il-warn, #a9741a);
  }
  &--hot {
    border-left-color: var(--il-accent-2, #ec4899);
  }
  &--slip {
    border-left-color: var(--il-accent-2, #ec4899);
    background: var(--il-accent-soft, #f2eafe);
  }
  &--missed {
    border-left-color: transparent;
  }
}

.ic41-row__av {
  position: relative;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--il-metal-gradient, linear-gradient(135deg, #c4c8d2, #9aa0ae));
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  display: grid;
  place-items: center;
}

.ic41-row__dot {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--il-good, #2f7d5b);
  border: 2px solid var(--il-card, #fff);
}

.ic41-row__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.ic41-row__top {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.ic41-row__name {
  font-weight: 650;
  font-size: 13px;
  color: var(--il-ink, #161a2c);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ic41-row__pres {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--il-good, #2f7d5b);
  text-transform: none;
}

.ic41-row__line {
  font-size: 12px;
  color: var(--il-ink-3a, #6b7290);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ic41-row__ticket {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--il-accent, #7c3aed);
  background: var(--il-accent-soft, #f2eafe);
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 2px 7px;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    border-color: var(--il-accent, #7c3aed);
  }

  &:focus-visible {
    outline: 2px solid var(--il-accent, #7c3aed);
    outline-offset: 1px;
  }
}

.ic41-row__wait {
  flex: 0 0 auto;
  font-family: var(--il-fm, monospace);
  font-size: 11px;
  font-weight: 700;
  color: var(--il-ink-3a, #6b7290);
  padding: 2px 6px;
  border-radius: 6px;
  background: var(--il-silver-soft, #edeff3);

  // the timer weight/colour climbs with the wait
  &--warm {
    color: var(--il-warn, #a9741a);
    background: var(--il-warn-soft, #f6eede);
  }
  &--hot,
  &--slip {
    color: #fff;
    background: var(--il-accent-2, #ec4899);
  }
}
</style>
